import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@clerk/react";
import { isAdminUser } from "../data/adminUsers";
import { useNavigate, useLocation } from "react-router-dom";
import { getTopTenFastestShips } from "../data";
import { ShipPosition } from "../types/ShipPosition";
import { getGrossTonnage } from "../data/shipTonnage";

const RANK_STYLES = [
  {
    bar: "from-yellow-400 via-yellow-300 to-orange-400",
    number: "text-yellow-400",
    border: "border-yellow-500/40",
    bg: "bg-yellow-500/5",
    glow: "shadow-yellow-500/20 shadow-lg",
    label: { text: "LEADER", style: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40" },
  },
  {
    bar: "from-slate-400 via-slate-300 to-slate-200",
    number: "text-slate-300",
    border: "border-slate-500/30",
    bg: "bg-slate-500/5",
    glow: "",
    label: null,
  },
  {
    bar: "from-orange-500 via-orange-400 to-amber-400",
    number: "text-orange-400",
    border: "border-orange-500/30",
    bg: "bg-orange-500/5",
    glow: "",
    label: null,
  },
];

const DEFAULT_STYLE = {
  bar: "from-cyan-600 to-blue-700",
  number: "text-gray-500",
  border: "border-gray-800",
  bg: "",
  glow: "",
  label: null,
};

export default function RaceMode() {
  const [ships, setShips] = useState<ShipPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [secondsSince, setSecondsSince] = useState(0);

  const { isLoaded, getToken, has, userId } = useAuth();
  const isCaptain = isLoaded && (has({ plan: "vessel_vibes_captain" }) || isAdminUser(userId));
  const navigate = useNavigate();
  const location = useLocation();
  const justUpgraded = new URLSearchParams(location.search).get("upgraded") === "1";

  useEffect(() => {
    if (!isLoaded || !isCaptain) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      const token = await getToken();
      if (!token) return;
      const data = await getTopTenFastestShips(token);
      setShips(data);
      setLastUpdated(new Date());
      setSecondsSince(0);
      setLoading(false);
    };
    load();
    const refresh = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(refresh);
  }, [isLoaded, isCaptain, getToken]);

  useEffect(() => {
    if (!lastUpdated) return;
    const tick = setInterval(() => {
      setSecondsSince(Math.floor((Date.now() - lastUpdated.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [lastUpdated]);

  const maxSpeed = ships.length > 0 ? Math.max(...ships.map((s) => Number(s.SPEED))) : 1;

  if (!isLoaded) return <div className="min-h-screen bg-gray-950" />;

  if (!isCaptain) {
    return (
      <div className="min-h-screen bg-gray-950 text-white pt-16 flex flex-col items-center justify-center gap-4">
        <div className="text-4xl">🏁</div>
        <h2 className="text-xl font-bold">Race Mode is Captain only</h2>
        <p className="text-gray-500 text-sm">Subscribe to see the top 10 fastest ships at sea right now.</p>
        <a href="/captain" className="mt-2 px-5 py-2 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded transition-colors">
          Get Captain — $4.99/mo
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white pt-16">
      {/* Welcome banner for new Captains */}
      {justUpgraded && (
        <div className="bg-cyan-500/10 border-b border-cyan-500/20 px-6 py-3 text-center">
          <span className="text-cyan-400 text-sm font-medium">⚓ Welcome aboard, Captain! You now have full access.</span>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-gray-800 px-6 py-5">
        <div className="max-w-2xl mx-auto flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              🏁 Race Mode
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Top 10 fastest cruise ships at sea right now
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              {lastUpdated
                ? secondsSince < 60
                  ? `Updated ${secondsSince}s ago`
                  : `Updated ${Math.floor(secondsSince / 60)}m ago`
                : "Loading..."}
            </div>
            <p className="text-[11px] text-gray-600 italic">Speed figures are live AIS data</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64 text-gray-500 text-sm">
            Loading race data...
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence>
              {ships.map((ship, i) => {
                const style = i < 3 ? RANK_STYLES[i] : DEFAULT_STYLE;
                const speedPct = (Number(ship.SPEED) / maxSpeed) * 100;
                const isLeader = i === 0;
                const gt = getGrossTonnage(ship.SHIPNAME);
                const destination = ship.NEXT_PORT_NAME?.trim();

                return (
                  <motion.div
                    key={ship.SHIPNAME}
                    layoutId={ship.SHIPNAME}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    transition={{ duration: 0.35, delay: i * 0.04 }}
                    onClick={() =>
                      navigate("/", {
                        state: {
                          focusShip: {
                            lat: Number(ship.LAT),
                            lon: Number(ship.LON),
                            name: ship.SHIPNAME,
                          },
                        },
                      })
                    }
                    className={`relative rounded-xl border ${style.border} ${style.bg} ${style.glow} p-4 cursor-pointer hover:brightness-110 transition-all`}
                  >
                    {/* Leader shimmer effect */}
                    {isLeader && (
                      <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400/5 to-transparent"
                          animate={{ x: ["-100%", "200%"] }}
                          transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 3, ease: "easeInOut" }}
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-4">
                      {/* Rank number */}
                      <div className={`text-3xl font-black w-9 text-center tabular-nums shrink-0 ${style.number}`}>
                        {i + 1}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Ship name + speed */}
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-white text-sm leading-tight">
                                {ship.SHIPNAME}
                              </span>
                              {style.label && (
                                <span
                                  className={`text-[9px] font-black px-1.5 py-0.5 rounded border tracking-widest uppercase ${style.label.style}`}
                                >
                                  {style.label.text}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-xs text-gray-500">{ship.LINE}</span>
                              {destination && (
                                <span className="flex items-center gap-1 text-[11px] text-cyan-500/80 font-medium">
                                  <span>→</span>
                                  <span className="tracking-wide">{destination}</span>
                                </span>
                              )}
                              {gt && (
                                <span className="text-[11px] text-gray-600 tabular-nums">
                                  {gt.toLocaleString()} GT
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Speed */}
                          <div className="text-right shrink-0">
                            <span className={`text-3xl font-black tabular-nums leading-none ${i < 3 ? style.number : "text-white"}`}>
                              {ship.SPEED}
                            </span>
                            <span className="text-xs text-gray-500 ml-1">kn</span>
                          </div>
                        </div>

                        {/* Race bar */}
                        <div className="relative h-1.5 bg-gray-800 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full rounded-full bg-gradient-to-r ${style.bar}`}
                            initial={{ width: 0 }}
                            animate={{ width: `${speedPct}%` }}
                            transition={{
                              duration: 1,
                              delay: i * 0.06,
                              ease: [0.25, 1, 0.5, 1],
                            }}
                          />
                          {/* Bar shimmer */}
                          <motion.div
                            className="absolute inset-y-0 w-8 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                            animate={{ x: ["-32px", "700px"] }}
                            transition={{
                              duration: 1.2,
                              delay: 0.8 + i * 0.06,
                              ease: "easeOut",
                            }}
                          />
                        </div>

                        {/* Fuel cost */}
                        <div className="mt-1.5 text-[11px] text-gray-600 tabular-nums">
                          ${Number(ship.HOURLY_FUEL_COST ?? 0).toLocaleString()}/hr est. fuel
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {/* Footer note */}
            <p className="text-center text-[11px] text-gray-700 pt-4">
              Click any ship to find it on the map
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
